/**
 * License Request Guard - frontend API-level safeguard.
 *
 * Intercepts outgoing mutating requests (POST/PUT/DELETE/PATCH) and blocks
 * them if the org's license doesn't cover the target section/feature.
 *
 * This is an ADDITIONAL layer on top of:
 *   - Route-level gating (LicenseGatedRoute)
 *   - Component-level gating (useLicenseGate hook)
 *   - Backend decorator gating (@require_section, @require_ai_feature)
 *
 * It provides defense-in-depth: even if a UI button isn't properly gated,
 * the API call won't fire.
 */
import { AxiosRequestConfig } from "axios";
import store from "../store";
import { accessAiFeatures, accessSections, isLicensingEnforced } from "../store/actions/PlanActions";

/* ─────────────────────────────────────────────────────────
   URLs that are ALWAYS allowed (infra, auth, settings, etc.)
   Checked first for fast-path exit.
   ───────────────────────────────────────────────────────── */
const ALWAYS_ALLOWED: RegExp[] = [
  /^\/auth/,
  /^\/licensing/,
  /^\/user-profile/,
  /^\/user/,
  /^\/settings/,
  /^\/notifications/,
  /^\/health/,
  /^\/feedback/,
  /^\/support/,
  /^\/payments/,
  /^\/onboarding/,
  /^\/menu/,
  /^\/dropdowns/,
  /^\/roles/,
  /^\/permissions/,
  /^\/webpubsub/,
  /^\/admin/,
  /^\/system/,
  /^\/errors/,
  /^\/devices/,
  /^\/recycle/,
  /^\/audit/,
  /^\/organization/,
  /^\/external/,
];

/* ─────────────────────────────────────────────────────────
   READ-ONLY is enforced by the backend alone. Many of its POST
   routes are reads (/matters/details, /matters/notes, …), and
   only the server knows which (`@read_only_safe`), so a client
   copy of "what counts as a write" drifts and blocks viewing.
   The server's LICENSE_READ_ONLY 403 reaches the error handler,
   which shows the renewal modal.
   ───────────────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────────────
   AI feature gates - checked BEFORE section gates because
   they're more specific (an AI endpoint within a section).
   ───────────────────────────────────────────────────────── */
const API_AI_FEATURE_MAP: Array<{ pattern: RegExp; feature: string }> = [
  /* Matter AI (`matter_ai`) - the backend's @require_matter_ai routes. A
     Starter matter is created and kept without AI; only these refuse. */
  { pattern: /^\/matters\/[^/]+\/analyze/,               feature: "matter_ai" },
  { pattern: /^\/matters\/[^/]+\/generate-more/,         feature: "matter_ai" },
  { pattern: /^\/contracts\/[^/]+\/extract/,             feature: "matter_ai" },
  // Drafts AI
  { pattern: /^\/drafts\/generate/,                       feature: "drafts" },
  { pattern: /^\/draft\/.*\/custom_instruction/,          feature: "drafts" },
  { pattern: /^\/matter-draft\/.*\/custom_instruction/,   feature: "drafts" },
  // Insights AI
  { pattern: /^\/insights\/generate/,                     feature: "insights" },
  { pattern: /^\/insights\/[^/]+\/[^/]+$/,                feature: "insights" },
  { pattern: /^\/matter-insight\/.*\/custom_instruction/,  feature: "insights" },
  // Tables AI processing
  { pattern: /^\/tables\/[^/]+\/process/,                 feature: "tables_ai" },
  { pattern: /^\/tables\/[^/]+\/process-column/,          feature: "tables_ai" },
  { pattern: /^\/tables\/[^/]+\/process-rows/,            feature: "tables_ai" },
  // Template AI
  { pattern: /^\/templates\/structures\/create$/,         feature: "template_ai" },
  { pattern: /^\/templates\/structure\/[^/]+\/reanalyze/, feature: "template_ai" },
  { pattern: /^\/templates\/branding\/extract/,           feature: "template_ai" },
  // Review AI
  { pattern: /^\/reviews\/[^/]+\/start/,                  feature: "review_ai" },
  { pattern: /^\/reviews\/[^/]+\/re-review/,              feature: "review_ai" },
  // Data source indexing
  { pattern: /^\/datasources\/create/,                    feature: "data_source_indexing" },
  { pattern: /^\/datasources\/[^/]+\/index/,              feature: "data_source_indexing" },
  // Client onboarding AI
  /* Also not gated, and for the same reason: pipeline is not sold, so the
     client pre-check would refuse a call for a feature nobody has bought
     access to yet. The server still decides. */
];

/* ─────────────────────────────────────────────────────────
   Section gates - maps API URL prefix to license section.
   Only mutating requests (POST/PUT/DELETE/PATCH) are checked.
   GETs always pass through for read-only access.
   ───────────────────────────────────────────────────────── */
const API_SECTION_MAP: Array<{ pattern: RegExp; section: string }> = [
  /* Matter economics routes that live UNDER /matters/<uuid> - before the
     /matters prefix, which would otherwise claim them for `matters`. */
  { pattern: /^\/matters\/[^/]+\/economics/,       section: "matter_economics" },
  { pattern: /^\/matters\/[^/]+\/time-entries/,    section: "matter_economics" },
  { pattern: /^\/matters\/[^/]+\/rate-overrides/,  section: "matter_economics" },
  /* F-REPORTS: the firm economics PDF is a POST, so it is checked here. (My
     time's report is under /users, which ALWAYS_ALLOWED leaves to the server.) */
  { pattern: /^\/organizations\/me\/economics\/report/, section: "analytics" },
  // Matters
  { pattern: /^\/matters/,                section: "matters" },
  /* Contracts: the backend gates the blueprint on `matters`. */
  { pattern: /^\/contracts/,              section: "matters" },
  // Marketplace (Founding)
  { pattern: /^\/marketplace/,            section: "marketplace" },
  { pattern: /^\/cases/,                  section: "matters" },
  // Contacts (formerly Clients)
  { pattern: /^\/clients/,               section: "contacts" },
  { pattern: /^\/contacts/,              section: "contacts" },
  // Tasks
  { pattern: /^\/tasks/,                 section: "tasks" },
  // Files / Documents
  { pattern: /^\/files/,                 section: "files" },
  { pattern: /^\/documents/,             section: "files" },
  // Research
  { pattern: /^\/research/,              section: "research" },
  // Tables
  { pattern: /^\/tables/,               section: "tables" },
  // Analytics
  { pattern: /^\/analytics/,            section: "analytics" },
  // Data Sources / Knowledge Base
  { pattern: /^\/datasources/,          section: "data_sources" },
  { pattern: /^\/data-sources/,         section: "data_sources" },
  // Templates
  { pattern: /^\/templates/,            section: "templates" },
  /* Drafting is a TOP-LEVEL page (AB#1573) with a section of its own, so its
     calls gate on `drafting` rather than borrowing `matters`. Insights rides
     the same section: `/drafting/insights/*` is one product and one blueprint
     with `/drafting/drafts/*` (backend efb8f20). */
  { pattern: /^\/drafting/,             section: "drafting" },
  /* `/drafts/*` is the OTHER blueprint - matter-scoped drafts - and the
     backend gates it on `matters` (domains/drafts/routes.py). Mapping it to
     `drafting` blocked a Starter firm's matter drafts in the browser for a
     section the server never asks for. */
  { pattern: /^\/drafts/,               section: "matters" },
  { pattern: /^\/draft\//,              section: "matters" },
  { pattern: /^\/matter-draft/,         section: "matters" },
  { pattern: /^\/engagement_letter/,    section: "matters" },
  // Insights
  { pattern: /^\/insights/,             section: "matters" },
  { pattern: /^\/matter-insight/,       section: "matters" },
  /* Client Pipeline is NOT gated. It is a separate feature from Projects and
     is not sold yet, so nothing here should refuse its calls. I previously
     mapped it to `projects` because the backend aliases the retired
     `client_pipeline` grant key there - but that alias is about a dead key,
     not about what the page is. An unmapped path is an open one. */
  /* Budgets, time entries and matter pricing - the €19 module, NOT `finance`,
     which Starter already carries for invoices and payments. */
  { pattern: /^\/matter-economics/,     section: "matter_economics" },
  { pattern: /^\/economics/,            section: "matter_economics" },
  // Finance / Billing
  { pattern: /^\/finance/,              section: "finance" },
  { pattern: /^\/billing/,              section: "finance" },
  // People / Teams / Departments
  { pattern: /^\/org-people\/teams/,          section: "teams" },
  { pattern: /^\/org-people\/departments/,    section: "departments" },
  { pattern: /^\/org-people/,                 section: "people" },
  // Review / Playbook
  { pattern: /^\/reviews/,              section: "reviews" },
  { pattern: /^\/playbooks/,            section: "reviews" },
  // Calendar
  { pattern: /^\/calendar/,             section: "calendar" },
  // Messages / Chat
  { pattern: /^\/chat/,                 section: "messages" },
  { pattern: /^\/messages/,             section: "messages" },
];

/* ─────────────────────────────────────────────────────────
   Guard function - called from the axios request interceptor.
   Returns { allowed: true } or { allowed: false, code, ... }.
   ───────────────────────────────────────────────────────── */
export function checkLicenseBeforeRequest(
  config: AxiosRequestConfig
): { allowed: true } | { allowed: false; code: string; section?: string; feature?: string } {
  // Only gate mutating methods
  const method = (config.method || "get").toUpperCase();
  if (method === "GET" || method === "HEAD" || method === "OPTIONS") {
    return { allowed: true };
  }

  const url = config.url || "";
  // Interceptor receives relative URLs (e.g. "/matters/create"). Some callers
  // omit the leading slash (`research/chat-groups`), which no pattern here
  // would match - so normalise it, and drop any query string.
  const rawPath = url.startsWith("http") ? new URL(url).pathname : url.split("?")[0];
  const path = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;

  // Read license state from Redux store (synchronous - no React needed)
  const state = store.getState();
  const access = state.license?.access;

  // Fast-path: always-allowed endpoints
  for (const re of ALWAYS_ALLOWED) {
    if (re.test(path)) return { allowed: true };
  }

  /* Don't block unless the BACKEND says licensing is enforced for this org -
     which also means "loaded": not loaded reads as not enforced, so a missing
     or failed /my-access never blocks a request. The server still refuses, and
     its 403 reaches the error handler. Only believable payloads are ever
     stored (see `isUsableAccess`), so an empty section list here is a real
     "no" - an expired org. */
  if (!isLicensingEnforced(state) || !access) return { allowed: true };

  // Hard floor: a user without a base seat cannot mutate ANY licensed
  // resource. The ALWAYS_ALLOWED list above covers the carve-outs they
  // still need (auth, settings, /licensing/feature-request, payments,
  // notifications, admin/account pages, etc.). Anything else falls through
  // to the NoSeatGate placeholder on the frontend.
  const hasBaseSeat = access.has_base_seat ?? true;
  if (!hasBaseSeat) {
    return { allowed: false, code: "NO_BASE_SEAT" };
  }

  const licensedSections = accessSections(access);
  const licensedAiFeatures = accessAiFeatures(access);

  /* `matter_ai` is not seeded on any row yet; until the backend rollout is
     everywhere, `insights` implies it (see `hasMatterAiAccess`). */
  const hasFeature = (feature: string) =>
    licensedAiFeatures.includes(feature) ||
    (feature === "matter_ai" && licensedAiFeatures.includes("insights"));

  // 1) Check AI feature gates first (more specific than section gates)
  for (const { pattern, feature } of API_AI_FEATURE_MAP) {
    if (pattern.test(path)) {
      if (!hasFeature(feature)) {
        return { allowed: false, code: "AI_FEATURE_NOT_LICENSED", feature };
      }
      // AI feature is licensed - still need to check the section below
      break;
    }
  }

  // 2) Check section gates
  for (const { pattern, section } of API_SECTION_MAP) {
    if (pattern.test(path)) {
      if (!licensedSections.includes(section)) {
        return { allowed: false, code: "SECTION_NOT_LICENSED", section };
      }
      return { allowed: true };
    }
  }

  // No matching rule - unmapped endpoints are not restricted
  return { allowed: true };
}
